import { useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowRight, Bot, Check, Flag, Lock } from "lucide-react";
import { useMemo } from "react";
import { toast } from "sonner";

import { BulkActionBar } from "@/components/cinch/BulkActionBar";
import {
  DataTable,
  type DataTableColumn,
} from "@/components/cinch/DataTable/DataTable";
import {
  CheckboxList,
  FilterBar,
  FilterChip,
  FilterClearAll,
  FilterSearch,
} from "@/components/cinch/FilterBar";
import { IconButton } from "@/components/cinch/IconButton";
import { KpiTile } from "@/components/cinch/KpiTile";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { cn } from "@/lib/utils";

import type { ReconStepSearch } from "..";
import {
  monthLabel,
  useBulkReviewParentChild,
  useParentChild,
  useReviewParentChild,
  type ParentChildItem,
  type ReviewStatus,
} from "../api";
import { ParentChildDrawer } from "./ParentChildDrawer";

const REVIEW_LABELS: { value: ReviewStatus; label: string }[] = [
  { value: "pending", label: "Pending" },
  // The shared status, in this step's words: the reviewer is confirming the
  // child really belongs under the parent, not re-approving the request.
  { value: "approved", label: "Verified" },
  { value: "flagged", label: "Flagged" },
];

/** R-16 worklist: every parent/child relationship change that moved in the
 *  close month — requested, approved, verified, rejected or withdrawn.
 *
 *  No pull. The relationship workflow's tables are ours, in the same database,
 *  so the list is always current — which matters here because an approved
 *  change sits un-applied until someone works the Cinch admin, and a snapshot
 *  would freeze it as "waiting" long after it went live. Only the verdict
 *  persists; completing the step freezes the month's changes as the audit copy.
 *
 *  The month comes from the CHANGELOG, not the request date: a link requested in
 *  June and verified in July is July's business, because July is when the tree
 *  changed. The same request can therefore be reviewed in two closes, once per
 *  month it moved in. */
export function ParentChildPanel({ period }: { period: string }) {
  const { data, isLoading, error } = useParentChild(period);
  const review = useReviewParentChild(period);
  const bulk = useBulkReviewParentChild(period);

  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as ReconStepSearch;
  const setSearch = (next: Partial<ReconStepSearch>) =>
    navigate({
      to: "/reconciliation/$period/$stepKey",
      params: { period, stepKey: "R-16" },
      search: (prev: ReconStepSearch) => ({ ...prev, ...next }),
      replace: true,
    });

  // `mrr` is the shared boolean slot in ReconStepSearch; here it means
  // "only changes Cinch hasn't confirmed yet".
  const awaitingOnly = !!search.mrr;
  const stageFilter = useMemo(
    () => (search.stage ? search.stage.split(",") : []),
    [search.stage],
  );
  const reviewFilter = useMemo(
    () => (search.review ? search.review.split(",") : []),
    [search.review],
  );
  const q = search.q ?? "";

  const items = useMemo(() => data?.items ?? [], [data]);
  const summary = data?.summary;
  const canAct = (data?.can_act ?? false) && !(data?.locked ?? false);

  // Stages with nothing in them drop out: the tab strip's job is showing what
  // the month actually did, and a row of zeroes competes with that.
  const liveStages = useMemo(
    () =>
      (data?.stages ?? []).filter(
        (s) => (summary?.counts_by_stage?.[s.key] ?? 0) > 0,
      ),
    [data?.stages, summary],
  );

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((i) => {
      if (stageFilter.length && !stageFilter.includes(i.stage)) return false;
      if (reviewFilter.length && !reviewFilter.includes(i.review_status)) return false;
      if (awaitingOnly && !i.awaiting_apply) return false;
      if (!needle) return true;
      return [
        i.child_name,
        i.parent_name,
        i.new_parent_name,
        i.reason,
        i.requested_by_name,
        i.approved_by_name,
      ]
        .filter(Boolean)
        .some((v) => (v as string).toLowerCase().includes(needle));
    });
  }, [items, stageFilter, reviewFilter, awaitingOnly, q]);

  const drawerItem = useMemo(
    () => items.find((i) => i.item_key === search.link) ?? null,
    [items, search.link],
  );

  function openDrawer(item: ParentChildItem, flag: boolean) {
    setSearch({ link: item.item_key, mismatch: flag || undefined });
  }

  function bulkReview(status: ReviewStatus) {
    bulk.mutate(
      { itemKeys: rows.map((r) => r.item_key), status },
      {
        onSuccess: (r) =>
          toast.success(
            `${r.updated} change${r.updated === 1 ? "" : "s"} ${
              status === "approved" ? "verified" : status
            }`,
          ),
        onError: (e) =>
          toast.error("Couldn't apply the verdict", {
            description: (e as Error).message,
          }),
      },
    );
  }

  const columns: DataTableColumn<ParentChildItem>[] = [
    {
      key: "relationship",
      header: "Relationship",
      className: "max-w-[320px]",
      render: (i) => (
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold">
              {i.child_name ?? i.child_company_id}
            </span>
            <span className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
              <ArrowRight className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">
                {i.action === "move"
                  ? (i.new_parent_name ?? i.new_parent_company_id ?? "—")
                  : (i.parent_name ?? i.parent_company_id)}
              </span>
            </span>
          </span>
        </div>
      ),
    },
    {
      key: "action",
      header: "Change",
      render: (i) => <span className="text-sm">{i.action_label}</span>,
    },
    {
      key: "stage",
      header: `In ${monthLabel(period)}`,
      render: (i) => (
        <span className="inline-flex items-center gap-1.5">
          {/* 'verified' isn't in StatusBadge's map (it would fall through to
              info-blue), so the tone comes from the mapped status and the WORD
              from the label — the documented way to use it. */}
          <StatusBadge
            value={i.stage === "verified" ? "approved" : i.stage}
            label={i.stage_label}
          />
        </span>
      ),
    },
    {
      key: "when",
      header: "Moved",
      render: (i) => {
        const at = i.verified_at ?? i.approved_at ?? i.requested_at;
        return (
          <span className="tabular-nums text-sm">
            {at ? new Date(at).toLocaleDateString() : "—"}
          </span>
        );
      },
    },
    {
      key: "who",
      header: "By",
      render: (i) => (
        <span className="text-sm">
          {i.approved_by_name ?? i.requested_by_name ?? "—"}
        </span>
      ),
    },
    {
      key: "applied",
      header: "Live in Cinch",
      render: (i) =>
        i.awaiting_apply ? (
          <span
            className="font-medium text-warning"
            title="Approved, but Cinch hasn't confirmed it — company relationships have no write API, so someone has to apply it in the Cinch admin and verify."
          >
            waiting
          </span>
        ) : i.applied_at ? (
          <span className="text-sm text-success">
            {new Date(i.applied_at).toLocaleDateString()}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
    {
      key: "ai_status",
      header: "AI",
      align: "center",
      className: "w-14",
      render: (i) =>
        i.ai_status ? (
          <Bot
            className={cn(
              "mx-auto h-4 w-4",
              i.ai_status === "flagged" ? "text-warning" : "text-success",
            )}
            aria-label={`AI review of the parent group: ${i.ai_status}`}
          />
        ) : null,
    },
    {
      key: "review",
      header: "Review",
      align: "right",
      className: "w-40",
      render: (i) => (
        <span className="inline-flex items-center justify-end gap-1.5">
          <StatusBadge
            value={i.review_status}
            label={i.review_status === "approved" ? "Verified" : undefined}
          />
          {canAct && (
            <>
              <IconButton
                title="Verified — this child belongs under this parent"
                active={i.review_status === "approved"}
                activeClassName="text-success"
                hoverClassName="hover:bg-success-light hover:text-success"
                onClick={() =>
                  review.mutate(
                    { itemKey: i.item_key, status: "approved" },
                    {
                      onError: (err) =>
                        toast.error("Couldn't save review", {
                          description: (err as Error).message,
                        }),
                    },
                  )
                }
              >
                <Check className="h-3.5 w-3.5" />
              </IconButton>
              <IconButton
                title="Flag — the tree is wrong (add notes)"
                active={i.review_status === "flagged"}
                activeClassName="text-warning"
                hoverClassName="hover:bg-warning-light hover:text-warning"
                onClick={() => openDrawer(i, true)}
              >
                <Flag className="h-3.5 w-3.5" />
              </IconButton>
            </>
          )}
        </span>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Every parent/child change that moved in {monthLabel(period)} — read
          live from the relationship workflow, so it's current every time you
          open it.
        </p>
        {data?.locked && (
          <span className="flex items-center gap-1 text-xs font-medium text-success">
            <Lock className="h-3.5 w-3.5" /> Signed off — frozen
          </span>
        )}
      </div>

      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))" }}
      >
        <KpiTile
          label={`Tree changes · ${monthLabel(period)}`}
          value={String(summary?.change_count ?? 0)}
          sub={`${summary?.requested_count ?? 0} requested · ${summary?.approved_count ?? 0} approved · ${summary?.verified_count ?? 0} verified`}
        />
        <KpiTile
          label="Waiting on Cinch"
          value={String(summary?.awaiting_apply_count ?? 0)}
          tone={summary?.awaiting_apply_count ? "warning" : "positive"}
          info="Approved here but not yet confirmed live in Cinch. Company relationships have no write API — the change is a Django-admin action verified on read — so until it lands, every roll-up still reads the OLD tree."
          sub="approved, tree unchanged"
          onClick={() => setSearch({ mrr: awaitingOnly ? undefined : true })}
          selected={awaitingOnly}
        />
        <KpiTile
          label="Awaiting review"
          value={String(summary?.pending_review_count ?? 0)}
          tone={summary?.pending_review_count ? "warning" : "positive"}
          onClick={() =>
            setSearch({
              review: reviewFilter.includes("pending") ? undefined : "pending",
            })
          }
          selected={reviewFilter.includes("pending")}
        />
        <KpiTile
          label="Flagged"
          value={String(summary?.flagged_count ?? 0)}
          tone={summary?.flagged_count ? "danger" : "positive"}
          info="An open flag blocks completing this step — completing it is the claim that the month's parent/child tree is right."
          sub={
            summary?.ai_flagged_count
              ? `${summary.ai_flagged_count} on an AI-flagged parent group`
              : undefined
          }
          onClick={() =>
            setSearch({
              review: reviewFilter.includes("flagged") ? undefined : "flagged",
            })
          }
          selected={reviewFilter.includes("flagged")}
        />
      </div>

      {error != null && (
        <Alert variant="danger">
          <AlertTitle>Couldn't load the month's relationship changes</AlertTitle>
          <AlertDescription>
            {error instanceof Error ? error.message : String(error)}
          </AlertDescription>
        </Alert>
      )}

      {/* One tab per stage the month actually produced, each with its count. */}
      {liveStages.length > 0 && (
        <div className="flex flex-wrap gap-1 border-b border-border">
          {[{ key: "", label: "All" }, ...liveStages].map((s) => {
            const on = s.key
              ? stageFilter.length === 1 && stageFilter[0] === s.key
              : stageFilter.length === 0;
            return (
              <button
                key={s.key || "all"}
                type="button"
                onClick={() => setSearch({ stage: s.key || undefined })}
                className={cn(
                  "flex items-center gap-1.5 rounded-t-md border-b-2 px-3 py-1.5 text-sm font-medium transition-colors",
                  on
                    ? "border-brand text-brand"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                )}
              >
                {s.label}
                <span className="rounded-full bg-muted px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-muted-foreground">
                  {s.key
                    ? (summary?.counts_by_stage?.[s.key] ?? 0)
                    : (summary?.change_count ?? 0)}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {!isLoading && items.length === 0 && (
        <Alert variant="success">
          <AlertTitle>No tree changes in {monthLabel(period)}</AlertTitle>
          <AlertDescription>
            Nobody requested, approved or verified a parent/child relationship
            this month, so there's nothing to review — the step can be completed
            as-is. A month with no changes is a clean month, not an unfinished
            one.
          </AlertDescription>
        </Alert>
      )}

      <FilterBar>
        <FilterSearch
          placeholder="Search company, parent, or reason…"
          value={q}
          onChange={(v) => setSearch({ q: v || undefined })}
        />
        <FilterChip label="Review" count={reviewFilter.length}>
          <CheckboxList
            options={REVIEW_LABELS}
            selected={reviewFilter}
            onChange={(v) => setSearch({ review: v.join(",") || undefined })}
          />
        </FilterChip>
        {(reviewFilter.length > 0 || stageFilter.length > 0 || awaitingOnly || q) && (
          <FilterClearAll
            onClick={() =>
              setSearch({
                review: undefined,
                stage: undefined,
                mrr: undefined,
                q: undefined,
              })
            }
          />
        )}
      </FilterBar>

      {canAct && rows.length > 0 && (
        <BulkActionBar
          count={rows.length}
          totalCount={items.length}
          noun="change"
          nounPlural="changes"
          actions={[
            {
              label: "Verified",
              tone: "success",
              icon: <Check className="h-3.5 w-3.5" />,
              disabled: bulk.isPending,
              onClick: () => bulkReview("approved"),
            },
            {
              label: "Flag",
              tone: "warning",
              icon: <Flag className="h-3.5 w-3.5" />,
              disabled: bulk.isPending,
              onClick: () => bulkReview("flagged"),
            },
          ]}
        />
      )}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(i) => i.item_key}
        loading={isLoading}
        emptyMessage={
          items.length === 0
            ? `No parent/child changes in ${monthLabel(period)}.`
            : "No changes match these filters."
        }
        onRowClick={(i) => openDrawer(i, false)}
        selectedKey={search.link ?? null}
        containerClassName="max-h-[calc(100vh-18rem)]"
      />

      <p className="text-xs text-muted-foreground">
        Click a row to see what changed, who moved it, and to leave notes.{" "}
        <b>Verified</b> means you checked the child really belongs under that
        parent; <b>flag</b> anything you can't accept — an open flag blocks
        completing R-16. Nothing is corrected here: company relationships have no
        write API, so a fix is a request in the Parent Companies monitor, applied
        in the Cinch admin and verified on read.
      </p>

      <ParentChildDrawer
        period={period}
        item={drawerItem}
        canAct={canAct}
        locked={data?.locked ?? false}
        expanded={!!search.expanded}
        presetFlag={!!search.mismatch}
        onExpandedChange={(v) => setSearch({ expanded: v || undefined })}
        onClose={() =>
          setSearch({ link: undefined, expanded: undefined, mismatch: undefined })
        }
      />
    </div>
  );
}
